import React, { useEffect, useMemo, useState } from "react";
import { API_ORIGIN, buildPublicFileUrl } from "../config/api";

/**
 * ResponsiveImage
 * ---------------------------------------------------------
 * Purpose:
 * - Automatically loads optimized uploaded images.
 * - Supports AVIF → WebP → JPG fallback.
 * - Supports MayaChitro public product folder:
 *   /uploads/public/products/[brand/category/product-folder]/[image-base].avif
 *
 * Image naming examples:
 *
 * Full:
 * main.avif
 * main.webp
 * main.jpg
 *
 * Card:
 * main-card.avif
 * main-card.webp
 * main-card.jpg
 *
 * Thumb:
 * main-thumb.avif
 * main-thumb.webp
 * main-thumb.jpg
 */

const DEFAULT_BACKEND_URL = API_ORIGIN;

const DEFAULT_UPLOAD_ROOT =
  process.env.REACT_APP_PUBLIC_PRODUCT_UPLOAD_ROOT || "/uploads/public/products";

function cleanBackendUrl(url) {
  return String(url || "")
    .replace(/\/api\/?$/, "")
    .replace(/\/$/, "");
}

function cleanPathPart(value) {
  return String(value || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
}

function ensureLeadingSlash(value) {
  const cleaned = String(value || "").trim();

  if (!cleaned) return "";

  return cleaned.startsWith("/") ? cleaned : `/${cleaned}`;
}

function isExternalUrl(value) {
  return /^https?:\/\//i.test(String(value || ""));
}

function removeQueryAndHash(value) {
  return String(value || "").split("?")[0].split("#")[0];
}

function normalizeUploadPath(value) {
  return String(value || "").replace(
    /\/uploads\/public\/products\/mayachitro\/photo-downloads\//gi,
    "/uploads/public/products/"
  );
}

function getBaseName(value) {
  const cleaned = removeQueryAndHash(value).trim();

  if (!cleaned) return "main";

  const fileName = cleaned.split("/").pop() || cleaned;

  return fileName
    .replace(/\.(avif|webp|jpg|jpeg|png)$/i, "")
    .replace(/-card$/i, "")
    .replace(/-thumb$/i, "");
}

function getVariantBaseName(imageBase, variant) {
  const base = getBaseName(imageBase || "main");

  if (variant === "card") return `${base}-card`;
  if (variant === "thumb") return `${base}-thumb`;

  return base;
}

function buildDirectImageUrl(path, backendUrl = DEFAULT_BACKEND_URL) {
  if (!path) return "";

  const value = normalizeUploadPath(String(path).trim());

  if (!value) return "";

  if (isExternalUrl(value)) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    const normalized = normalizeUploadPath(value);
    return backendUrl === DEFAULT_BACKEND_URL
      ? buildPublicFileUrl(normalized)
      : `${cleanBackendUrl(backendUrl)}${normalized}`;
  }

  if (value.startsWith("uploads/")) {
    const normalized = `/${normalizeUploadPath(value)}`;
    return backendUrl === DEFAULT_BACKEND_URL
      ? buildPublicFileUrl(normalized)
      : `${cleanBackendUrl(backendUrl)}${normalized}`;
  }

  return value;
}

function inferFolderName(product) {
  return (
    product?.folder_name ||
    product?.folderName ||
    product?.image_folder ||
    product?.imageFolder ||
    product?.product_folder ||
    product?.productFolder ||
    product?.folder ||
    ""
  );
}

function inferImageBase(product) {
  return (
    product?.main_image_base ||
    product?.mainImageBase ||
    product?.image_base ||
    product?.imageBase ||
    product?.mainImage ||
    product?.main_image ||
    product?.coverImage ||
    product?.cover_image ||
    product?.previewImage ||
    product?.preview_image ||
    "main"
  );
}

function inferPublicBasePath(product) {
  return (
    product?.public_base_path ||
    product?.publicBasePath ||
    product?.public_path ||
    product?.publicPath ||
    product?.image_base_path ||
    product?.imageBasePath ||
    ""
  );
}

function buildFolderPath({
  product,
  folderName,
  uploadRoot = DEFAULT_UPLOAD_ROOT,
}) {
  const resolvedFolderName = cleanPathPart(normalizeUploadPath(folderName || inferFolderName(product)));
  const publicBasePath = cleanPathPart(normalizeUploadPath(inferPublicBasePath(product)));
  const cleanedUploadRoot = cleanPathPart(uploadRoot);

  if (!resolvedFolderName && !publicBasePath) {
    return "";
  }

  /**
   * Case 1:
   * folderName already contains full upload path:
   * uploads/public/products/mayachitro/photo-downloads/MC-0001...
   */
  if (
    resolvedFolderName.startsWith("uploads/") ||
    resolvedFolderName.startsWith("public/") ||
    resolvedFolderName.includes("/uploads/")
  ) {
    return ensureLeadingSlash(resolvedFolderName);
  }

  /**
   * Case 2:
   * publicBasePath already points to product folder:
   * uploads/public/products/.../MC-0001-product-name
   */
  if (publicBasePath && resolvedFolderName) {
    const normalizedPublicBase = publicBasePath.toLowerCase();
    const normalizedFolder = resolvedFolderName.toLowerCase();

    if (
      normalizedPublicBase.endsWith(`/${normalizedFolder}`) ||
      normalizedPublicBase === normalizedFolder
    ) {
      return ensureLeadingSlash(publicBasePath);
    }

    return ensureLeadingSlash(`${publicBasePath}/${resolvedFolderName}`);
  }

  /**
   * Case 3:
   * publicBasePath exists but folderName is empty.
   */
  if (publicBasePath && !resolvedFolderName) {
    return ensureLeadingSlash(publicBasePath);
  }

  /**
   * Case 4:
   * normal default:
   * /uploads/public/products/[folderName]
   */
  return ensureLeadingSlash(`${cleanedUploadRoot}/${resolvedFolderName}`);
}

function buildUploadedImageUrl({
  product,
  folderName,
  imageBase = "main",
  variant = "full",
  extension = "avif",
  uploadRoot = DEFAULT_UPLOAD_ROOT,
  backendUrl = DEFAULT_BACKEND_URL,
}) {
  const folderPath = buildFolderPath({
    product,
    folderName,
    uploadRoot,
  });

  if (!folderPath) return "";

  const fileBase = getVariantBaseName(imageBase, variant);

  const filePath = `${folderPath}/${fileBase}.${extension}`;
  return backendUrl === DEFAULT_BACKEND_URL
    ? buildPublicFileUrl(filePath)
    : `${cleanBackendUrl(backendUrl)}${filePath}`;
}

function ResponsiveImage({
  product,
  folderName,
  imageBase,
  variant = "card",
  uploadRoot = DEFAULT_UPLOAD_ROOT,
  backendUrl = DEFAULT_BACKEND_URL,

  src,
  fallbackSrc,

  alt = "",
  className = "",
  imgClassName = "",
  style,
  imgStyle,

  loading = "lazy",
  decoding = "async",
  fetchPriority,
  sizes,

  objectFit,
  objectPosition = "center",

  onClick,
  onError,
  ...restProps
}) {
  const resolvedFolderName = folderName || inferFolderName(product);
  const resolvedImageBase = imageBase || inferImageBase(product);

  const imageSources = useMemo(() => {
    if (src) {
      return [buildDirectImageUrl(src, backendUrl)].filter(Boolean);
    }

    const optimizedUrls = [
      buildUploadedImageUrl({
        product,
        folderName: resolvedFolderName,
        imageBase: resolvedImageBase,
        variant,
        extension: "avif",
        uploadRoot,
        backendUrl,
      }),
      buildUploadedImageUrl({
        product,
        folderName: resolvedFolderName,
        imageBase: resolvedImageBase,
        variant,
        extension: "webp",
        uploadRoot,
        backendUrl,
      }),
      buildUploadedImageUrl({
        product,
        folderName: resolvedFolderName,
        imageBase: resolvedImageBase,
        variant,
        extension: "jpg",
        uploadRoot,
        backendUrl,
      }),
    ].filter(Boolean);

    const directFallback = buildDirectImageUrl(fallbackSrc, backendUrl);

    if (directFallback) {
      optimizedUrls.push(directFallback);
    }

    return [...new Set(optimizedUrls)];
  }, [
    product,
    src,
    fallbackSrc,
    backendUrl,
    resolvedFolderName,
    resolvedImageBase,
    variant,
    uploadRoot,
  ]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [hasFailed, setHasFailed] = useState(false);
  const imageSourcesKey = imageSources.join("|");

  useEffect(() => {
    setActiveIndex(0);
    setHasFailed(false);
  }, [imageSourcesKey]);

  const activeSrc = imageSources[activeIndex] || "";

  const handleImageError = () => {
    const nextIndex = activeIndex + 1;

    if (nextIndex < imageSources.length) {
      setActiveIndex(nextIndex);
      return;
    }

    setHasFailed(true);
    onError?.();
  };

  const finalObjectFit =
    objectFit || (variant === "full" ? "contain" : "cover");

  const finalImgStyle = {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: finalObjectFit,
    objectPosition,
    ...imgStyle,
  };

  if (!activeSrc || hasFailed) {
    return (
      <div
        className={`responsive-image responsive-image--missing ${className}`}
        style={{
          width: "100%",
          height: "100%",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          background:
            "linear-gradient(135deg, rgba(10,15,16,0.96), rgba(21,28,29,0.96))",
          color: "rgba(255,255,255,0.58)",
          fontSize: "0.82rem",
          fontWeight: 700,
          textAlign: "center",
          borderRadius: "inherit",
          ...style,
        }}
        onClick={onClick}
        {...restProps}
      >
        Image unavailable
      </div>
    );
  }

  return (
    <div
      className={`responsive-image ${className}`}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: "inherit",
        ...style,
      }}
      onClick={onClick}
      {...restProps}
    >
      <img
        src={activeSrc}
        alt={alt}
        className={imgClassName}
        style={finalImgStyle}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        sizes={sizes}
        onError={handleImageError}
        draggable="false"
      />
    </div>
  );
}

export default ResponsiveImage;
